import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Select, Popconfirm, notification, Typography, Space, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import { INVOICE_STATUS_COLORS } from './InvoicesPage';

import { date as fmtDate, label } from '../../lib/format';
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Firm-wide receipts register - every payment recorded against an invoice (spec §26-27). */
export default function PaymentsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayments = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/payments', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => r.status && setPayments(r.data))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchPayments, [companyId]);

  const handleVoid = (payment) => {
    request
      .delete(`/payments/${payment.id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Receipt ${payment.receipt_number} voided` });
          fetchPayments();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  const columns = [
    { title: 'Receipt', dataIndex: 'receipt_number' },
    { title: 'Date', dataIndex: 'payment_date', sorter: (a, b) => (a.payment_date < b.payment_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Client', key: 'client', render: (_, p) => (p.Client ? <Link to={`/clients/${p.Client.id}`}>{p.Client.client_name}</Link> : '-') },
    {
      title: 'Invoice',
      key: 'invoice',
      render: (_, p) =>
        p.Invoice ? (
          <Space>
            <Link to={`/invoices/${p.Invoice.id}`}>{p.Invoice.invoice_number}</Link>
            <Tag color={INVOICE_STATUS_COLORS[p.Invoice.status]}>{p.Invoice.status.replace('_', ' ')}</Tag>
          </Space>
        ) : (
          '-'
        ),
    },
    { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
    { title: 'Method', dataIndex: 'method', render: (v) => label(v) },
    { title: 'Reference', dataIndex: 'reference' },
    { title: 'Recorded by', key: 'by', render: (_, p) => p.CreatedBy?.name || '-' },
    ...(isManager
      ? [
          {
            title: '',
            key: 'actions',
            render: (_, p) => (
              <Popconfirm title={`Void receipt ${p.receipt_number}?`} onConfirm={() => handleVoid(p)}>
                <Button size="small" danger icon={<DeleteOutlined />}>
                  Void
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Payments / Receipts
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Typography.Text type="secondary">Payments are recorded from an issued invoice's page.</Typography.Text>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={payments} loading={loading} />
    </div>
  );
}
